import { ineligibleIdsArray } from 'services/utils';
import { User } from 'types/user';
import { normalizeFuncionarioId } from 'utils/helpers';

import { hasHybridWorkAgreement } from './target-eligibility';

const GRUPOS_FORA_DA_DINAMICA = [
  'LIDERANCA ESTRATEGICA',
  'CONSELHO DIRETOR E AUDITOR GERAL',
  'CONSELHO DIRETOR/GG UE/AUDITOR GERAL',
];

export interface ContextoDaInclusao {
  participantes: User[];
  /** Chaves dos gestores do autor (`userInfo.gestores`). */
  gestoresDoAutor?: string[];
  /** Chaves dos gestores do fluxo de apreciação da realização em edição. */
  gestoresDaTarefa: string[];
}

/**
 * `efeito` marca as recusas que, além do aviso, disparam algo no cadastro: `limite` registra a pessoa para o aviso de
 * limite e `preferencia` envia a notificação de preferência, como na inclusão pela busca.
 */
export type AvaliacaoDaInclusao = { aceita: true } | { aceita: false; motivo: string; efeito?: 'limite' | 'preferencia' };

function recusa(motivo: string, efeito?: 'limite' | 'preferencia'): AvaliacaoDaInclusao {
  return efeito ? { aceita: false, motivo, efeito } : { aceita: false, motivo };
}

/**
 * Regras para incluir uma pessoa na Realização Destaque, na ordem e com os avisos da inclusão pela busca (RCP-14).
 * A busca mostra o motivo num alerta; a colagem de chaves reúne os motivos num resumo.
 */
export function avaliarInclusao(pessoa: User, contexto: ContextoDaInclusao): AvaliacaoDaInclusao {
  if (GRUPOS_FORA_DA_DINAMICA.includes(pessoa?.extra?.grupo)) {
    return recusa(
      'Conforme o regulamento: Membros do Conselho Diretor, Diretores e Lideranças Estratégicas não participam da Dinâmica Realização Destaque.',
    );
  }

  if (contexto.participantes.some(participante => participante._id === pessoa._id)) {
    return recusa('Este funcionário já está na lista de participantes da Realização.');
  }

  if (!hasHybridWorkAgreement(pessoa?.extra?.tipo_de_acordo)) {
    return recusa(
      'Conforme o regulamento, somente funcionários com Acordo de Trabalho do tipo híbrido podem ser incluídos na Realização.',
    );
  }

  const chave = normalizeFuncionarioId(pessoa?._id);
  const gestores = [...(contexto.gestoresDoAutor ?? []), ...contexto.gestoresDaTarefa];

  if (gestores.some(gestor => normalizeFuncionarioId(gestor) === chave)) {
    return recusa('Esta pessoa não pode ser indicada nesta Realização, pois faz parte do fluxo de apreciação desta entrega.');
  }

  if (ineligibleIdsArray.includes(pessoa?.extra?.cod_funcao)) {
    return recusa('Esta pessoa não pode ser indicada em Realizações Destaque.');
  }

  if (!pessoa.canAddUser) {
    return recusa(
      'Este funcionário não pode ser adicionado a esta Realização, pois já está participando do número máximo de Realizações na fase.',
      'limite',
    );
  }

  if (pessoa?.extra?.task === false) {
    return recusa(`${pessoa?.extra?.nome_guerra} optou, em "Minhas Preferências", pela não participação nesta dinâmica.`, 'preferencia');
  }

  return { aceita: true };
}
